import * as React from "react";
import { useCallback, useState } from "react";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Label, ListGroup, ListGroupItem } from "components/bootstrap";
import { IconButton, Spinner } from "components/common";
import { ILLUMINATE_PACK_TYPES } from "../SelectIlluminateStep";
const ScrollableListWrapper = styled.div`
  position: relative;
`;
const Controls = styled.div(
  ({ theme }) => css`
    display: flex;
    justify-content: flex-start;
    gap: ${theme.spacings.xs};
    background-color: ${theme.colors.variant.lightest.default};
    color: ${theme.utils.contrastingColor(theme.colors.variant.lightest.default)};
    padding: ${theme.spacings.xs};
    margin-top: ${theme.spacings.xs};
  `
);
const ScrollableList = styled(ListGroup)`
  max-height: 425px;
  overflow-y: auto;
`;
const StyledListGroupItem = styled(ListGroupItem)(
  ({ theme }) => css`
    display: flex;
    padding-right: ${theme.spacings.xxs};
    justify-content: flex-start;
    gap: ${theme.spacings.xs};
  `
);
const EmptyListListGroupItem = styled(ListGroupItem)(
  ({ theme }) => css`
    padding-top: ${theme.spacings.md};
    padding-bottom: ${theme.spacings.md};
    text-align: center;
    font-style: italic;
  `
);
const StyledCheckbox = styled(Checkbox)(
  ({ $isExpanded }) => css`
    margin-top: 0;
    margin-bottom: 0;
    ${$isExpanded ? `
    align-self: flex-start;
  ` : `
    align-self: center;
  `}
  `
);
const TextWrapper = styled.div(
  ({ $isExpanded }) => css`
    width: 100%;
    ${$isExpanded ? `
    align-self: flex-start;
  ` : `
    align-self: center;
  `}
  `
);
const PackText = styled.p`
  margin-bottom: 0;
`;
const PackTitle = styled.span`
  display: inline;
  margin: 0;
  font-size: inherit;
`;
const StyledLabel = styled(Label)(
  ({ theme }) => css`
    margin-left: ${theme.spacings.xs};
  `
);
const IlluminateList = ({
  isLoading,
  listData,
  selectedPacks,
  setSelectedPacks,
  setTouched = void 0,
  type
}) => {
  const { t } = useTranslation("enterprise_forwarder");
  const [expandedPacks, setExpandedPacks] = useState([]);
  const isPackExpanded = useCallback((packId) => expandedPacks.includes(packId), [expandedPacks]);
  const isPackSelected = useCallback((packId) => selectedPacks.includes(packId), [selectedPacks]);
  const onToggleExpandPack = (packId) => {
    if (!isPackExpanded(packId)) {
      setExpandedPacks([...expandedPacks, packId]);
    } else {
      setExpandedPacks(expandedPacks.filter((pack) => pack !== packId));
    }
  };
  const onSelectPack = (packId) => {
    if (setTouched) {
      setTouched(true);
    }
    if (!isPackSelected(packId)) {
      setSelectedPacks([...selectedPacks, packId]);
    } else {
      setSelectedPacks(selectedPacks.filter((pack) => pack !== packId));
    }
  };
  const onSelectAll = () => {
    if (setTouched) {
      setTouched(true);
    }
    if (!listData) return;
    const allIds = listData.map(({ pack_id }) => pack_id);
    setSelectedPacks(allIds);
  };
  const onDeselectAll = () => {
    if (setTouched) {
      setTouched(true);
    }
    setSelectedPacks([]);
  };
  if (isLoading) return /* @__PURE__ */ React.createElement(Spinner, null);
  const noneSelected = selectedPacks.length <= 0;
  const allPacksSelected = selectedPacks.length === (listData == null ? void 0 : listData.length);
  const listLabel = type === ILLUMINATE_PACK_TYPES.PROCESSING ? t("illuminate_list.label_packs") : t("illuminate_list.label_spotlight_packs");
  return /* @__PURE__ */ React.createElement(ScrollableListWrapper, null, /* @__PURE__ */ React.createElement(Controls, null, /* @__PURE__ */ React.createElement(Button, { bsSize: "small", onClick: onSelectAll, disabled: allPacksSelected }, t("illuminate_list.select_all")), /* @__PURE__ */ React.createElement(Button, { bsSize: "small", onClick: onDeselectAll, disabled: noneSelected }, t("illuminate_list.clear"))), /* @__PURE__ */ React.createElement(ScrollableList, { "aria-label": listLabel }, listData.length > 0 ? listData.map((pack) => {
    const isExpanded = isPackExpanded(pack.pack_id);
    const titleId = `illuminate-pack-title-${pack.pack_id}`;
    const descriptionId = `illuminate-pack-description-${pack.pack_id}`;
    return /* @__PURE__ */ React.createElement(StyledListGroupItem, { key: `pack-${pack.pack_id}` }, /* @__PURE__ */ React.createElement(
      StyledCheckbox,
      {
        $isExpanded: isExpanded,
        checked: isPackSelected(pack.pack_id),
        onChange: () => onSelectPack(pack.pack_id),
        name: `checkbox_${pack.pack_id}`,
        title: pack.title,
        "aria-labelledby": titleId,
        "aria-describedby": isExpanded ? descriptionId : void 0
      }
    ), /* @__PURE__ */ React.createElement(TextWrapper, { $isExpanded: isExpanded }, /* @__PURE__ */ React.createElement(PackText, null, /* @__PURE__ */ React.createElement(PackTitle, { id: titleId, role: "heading", "aria-level": 4 }, pack.title), pack.enabled && /* @__PURE__ */ React.createElement(StyledLabel, { bsStyle: "primary" }, t("illuminate_list.enabled"))), isExpanded && /* @__PURE__ */ React.createElement(PackText, { id: descriptionId }, pack.description)), /* @__PURE__ */ React.createElement(
      IconButton,
      {
        onClick: () => onToggleExpandPack(pack.pack_id),
        title: isExpanded ? t("illuminate_list.hide_details", { title: pack.title }) : t("illuminate_list.read_more", { title: pack.title }),
        name: isExpanded ? "keyboard_arrow_up" : "keyboard_arrow_down"
      }
    ));
  }) : /* @__PURE__ */ React.createElement(EmptyListListGroupItem, null, type === ILLUMINATE_PACK_TYPES.PROCESSING && t("illuminate_list.no_content_processing"), type === ILLUMINATE_PACK_TYPES.SPOTLIGHT && t("illuminate_list.no_content_spotlight"))));
};
export default IlluminateList;
